/**
 * IBS drawer link-table sections (P15.3 / #416).
 *
 * IBSChannelLink wires the IBS to one or more delivery channels.
 * Used by the heatmap derivation; admin manages link via the
 * IBS detail drawer rather than a separate page.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "shared/api/client";

import type { IBSService } from "./ibsAdapter";
import { LinkSection } from "./LinkSection";

export function IBSChannelLinks({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  // P429.6 -- a Critical Internal Function has no delivery channel, and
  // the API refuses one; offering the control would be an action the
  // seat cannot complete.
  const row = useQuery({
    queryKey: ["foundation-admin", "ibses", rowId, "class"] as const,
    queryFn: () => api.get<IBSService>(`/v1/admin/foundation/ibses/${rowId}`),
  });
  if (row.data?.service_class === "internal") {
    return (
      <p data-testid="ibs-channel-links-function" className="text-xs text-slate-600">
        {t("modules.foundation.admin.ibses.channelLinks.function")}
      </p>
    );
  }
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.ibses.channelLinks"
      listEndpoint={`/v1/admin/foundation/ibses/${rowId}/channels`}
      deleteEndpoint={(linkId) =>
        `/v1/admin/foundation/ibses/${rowId}/channels/${linkId}`
      }
      optionsEndpoint="/v1/admin/foundation/channels"
      targetFieldKey="channel_id"
      queryKey={["foundation-admin", "ibses", rowId, "channels"] as const}
      optionsQueryKey={["foundation-admin", "channels", "options"] as const}
    />
  );
}

/* P429.2 -- decision paper v0.2 § 7.5: what the service funds itself
   from, which function funds it, and the regulatory commitments it is
   bound by or produces. Three edges, one section each. */

export function IBSFundingResourceLinks({ rowId }: { rowId: string }) {
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.ibses.fundingLinks"
      listEndpoint={`/v1/admin/foundation/ibses/${rowId}/funding-resources`}
      deleteEndpoint={(id) =>
        `/v1/admin/foundation/ibses/${rowId}/funding-resources/${id}`
      }
      optionsEndpoint="/v1/admin/foundation/funding-resources"
      targetFieldKey="funding_resource_id"
      queryKey={["foundation-admin", "ibses", rowId, "funding-resources"] as const}
      optionsQueryKey={["foundation-admin", "funding-resources", "options"] as const}
    />
  );
}

export function IBSFundedByLinks({ rowId }: { rowId: string }) {
  return (
    <LinkSection
      i18nPrefix="modules.foundation.admin.ibses.fundedByLinks"
      listEndpoint={`/v1/admin/foundation/ibses/${rowId}/funded-by`}
      deleteEndpoint={(id) => `/v1/admin/foundation/ibses/${rowId}/funded-by/${id}`}
      optionsEndpoint="/v1/admin/foundation/ibses?limit=500"
      targetFieldKey="cif_id"
      queryKey={["foundation-admin", "ibses", rowId, "funded-by"] as const}
      optionsQueryKey={["foundation-admin", "ibses", "options", "all"] as const}
      optionsFilter={(o) => o.service_class === "internal" && o.id !== rowId}
    />
  );
}

export function IBSRegulatoryLinks({ rowId }: { rowId: string }) {
  const { t } = useTranslation();
  const prefix = "modules.foundation.admin.ibses.regulatoryLinks";
  return (
    <LinkSection
      i18nPrefix={prefix}
      listEndpoint={`/v1/admin/foundation/ibses/${rowId}/regulatory`}
      deleteEndpoint={(id) => `/v1/admin/foundation/ibses/${rowId}/regulatory/${id}`}
      optionsEndpoint="/v1/admin/foundation/regulatory-commitments"
      targetFieldKey="regulatory_commitment_id"
      displayFieldKey="relation"
      createExtraFieldKey="relation"
      createExtraOptions={[
        { value: "bound_by", label: t(`${prefix}.relation.bound_by`) },
        { value: "generates", label: t(`${prefix}.relation.generates`) },
      ]}
      queryKey={["foundation-admin", "ibses", rowId, "regulatory"] as const}
      optionsQueryKey={
        ["foundation-admin", "regulatory-commitments", "options"] as const
      }
    />
  );
}
